import { useContext, useState, useEffect, useMemo } from "react";

import {
  Typography,
  useTheme,
  Container,
  Alert,
  Stack,
  Box,
} from "@mui/material";
import { isSameDay, isSameHour, isValid } from "date-fns";

import UrentLogo from "../../components/UrentLogo";

import { AppContext } from "../../contextProviders/AppContext";

import { translation } from "../../components/Dictionary";
import SelectProvider from "./SelectProvider";
import SelectDate from "../../components/Datepicker";
import RentalServiceSelector from "./RentalServiceSelector";

export default function ServiceProvider() {
  const theme = useTheme();

  const {
    activeStep,
    companies,
    logoVisibility,
    reservationReturn,
    reservationStart,
    selectedCompany,
    mobileViewPort,
    language,
    handleClearCart,
    setOffers,
    searchButtonError,
    selectedDateRange,

    endTimeOk,
    setSelectedPicker,
    selectedPicker,
    setInitSearch,
    initSearch,
    FEATURE_NO_TIME_SELECT,
  } = useContext(AppContext);

  // These handle the new calendar component, values are transfered to dateRange, that checks restrictions

  // ID of Vuokraapaku Company Sörnäinen
  const VUOKRAAPAKU_SPECIAL_COMPANY = useMemo(() => {
    const VUOKRAAPAKU_COMPANY_ID = "00ef9a27-c9ea-4947-9115-436433a83a35";
    return selectedCompany?.id === VUOKRAAPAKU_COMPANY_ID;
  }, [selectedCompany]);

  const [providerSelectOpen, setProviderSelectOpen] = useState(false);
  const FEATURE_URENT_LOGO = logoVisibility;

  const searchButtonColor = useMemo(() => {
    if (searchButtonError) return theme.palette.warning.light;
    if (
      !searchButtonError &&
      selectedDateRange[0] !== null &&
      selectedDateRange[1] !== null
    ) {
      return theme.palette.success.main;
    }

    return theme.palette.primary.main;
  }, [
    searchButtonError,
    selectedDateRange,
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.light,
  ]);
  // Control the heigth of the elements inside this container
  const contentHeigth = "56px";

  // close the calendar if provider select is open.
  useEffect(() => {
    if (providerSelectOpen) {
      setSelectedPicker(null);
    }
  }, [providerSelectOpen]);

  // open calendar when company is selected

  // check if dates from previous search and datepicker match
  // if not, customer is adviced to complete the new search
  const equalDates = useMemo(() => {
    // if there is no previous search, stay calm and keep on truckin
    if (activeStep > 2) return;
    if (!isValid(reservationStart)) return true;

    if (!isSameDay(reservationStart, selectedDateRange[0])) return false;
    if (!isSameDay(reservationReturn, selectedDateRange[1])) return false;
    if (!isSameHour(reservationStart, selectedDateRange[0])) return false;
    if (!isSameHour(reservationReturn, selectedDateRange[1])) return false;
    return true;
  }, [activeStep, selectedDateRange, reservationStart, reservationReturn]);

  const bgColor = useMemo(() => {
    if (selectedPicker || providerSelectOpen) {
      return theme.palette.secondary.serviceProviderBg;
    } else {
      return theme.palette.primary.serviceProviderBg;
    }
  }, [selectedPicker, providerSelectOpen, theme.palette]);

  /*   const fullScreenStyles = {
    position: "relative",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "lightgrey",
    borderRadius: "45px",
    padding: "5px",
    marginTop: "20px",
    boxShadow: theme.shadows[0],
    // bgcolor: bgColor,
    transition: "ease-in-out 150ms",
    marginBottom: "55px",
  };

  const mobileViewStyles = {
    position: "relative",
    borderStyle: "none",
    borderWidth: 1,
    borderColor: "lightgrey",
    borderRadius: "45px",
    boxShadow: theme.shadows[0],
    bgcolor: bgColor,
    transition: "ease-in-out 150ms",

    justifyContent: mobileViewPort ? "center" : "space-between",
    position: "relative",
  };
 */
  const containerFullScreenStyles = {
    height: "60px",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "lightgrey",
    borderRadius: "45px",
    padding: "5px",
    marginTop: "20px",
    boxShadow: theme.shadows[0],
    bgcolor: bgColor,
    flex: FEATURE_NO_TIME_SELECT ? 0.8 : 1,
    transition: "ease-in-out 250ms",
    display: "flex",
  };

  const containerMobileViewStyles = {
    borderRadius: "15px",
    padding: "5px",
    marginTop: "20px",
    boxShadow: theme.shadows[0],
    // bgcolor: "#f7f7f7",
    transition: "ease-in-out 150ms",
    display: "flex",
    flex: 1,
  };

  const searchButtonDesktopStyles = {
    width: FEATURE_NO_TIME_SELECT ? "25%" : "15%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textTransform: "none",
    fontWeight: "bolder",
    letterSpacing: 1,
    fontFamily: "Sofia Pro, sans serif",
    height: contentHeigth,
    /* width: "175px", */
    borderTopRightRadius: "45px",
    borderBottomRightRadius: " 45px",
    bgcolor: searchButtonColor,
    marginRight: "3px",
    cursor: "pointer",
    transition: "250ms ease-in-out",
    "&:hover": {
      boxShadow: theme.shadows[4],
    },
  };

  const searchButtonMobileStyles = {
    width: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textTransform: "none",
    fontWeight: "bolder",
    letterSpacing: 1,
    fontFamily: "Sofia Pro, sans serif",
    height: "45px",
    /* width: "175px", */
    borderRadius: "45px",
    marginTop: "15px",
    marginBottom: "15px",
    bgcolor: searchButtonColor,
    boxShadow: theme.shadows[4],
    cursor: "pointer",
  };

  useEffect(() => {
    if (selectedCompany && !isValid(selectedDateRange[0])) {
      setSelectedPicker("calendar");
    }
  }, [selectedCompany, endTimeOk.hasError, selectedDateRange, setOffers]);

  if (activeStep < 3 && companies)
    return (
      <>
        <Container
          style={{
            backgroundColor: theme.palette.primary.background,
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              sx={
                mobileViewPort
                  ? containerMobileViewStyles
                  : containerFullScreenStyles
              }
            >
              <Stack
                direction={mobileViewPort ? "column" : "row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                sx={{ flex: 1 }}
              >
                <RentalServiceSelector
                  setSelectedPicker={setSelectedPicker}
                  selectedPicker={selectedPicker}
                  contentHeigth={contentHeigth}
                  setProviderSelectOpen={setProviderSelectOpen}
                  providerSelectOpen={providerSelectOpen}
                />

                <SelectDate
                  contentHeigth={contentHeigth}
                  fullWidth={true}
                  style={{ height: 54 }}
                  startTime={selectedDateRange[0]}
                  endTime={selectedDateRange[1]}
                />
                <SearchButton
                  searchButtonDesktopStyles={searchButtonDesktopStyles}
                  searchButtonMobileStyles={searchButtonMobileStyles}
                />
              </Stack>
            </Box>
          </Box>
          {FEATURE_URENT_LOGO && (
            <Box
              sx={{
                display: "flex",

                justifyContent: "flex-end",
                marginRight: mobileViewPort ? "15px" : "30px",
              }}
            >
              <PoweredByUrent />
            </Box>
          )}
          {!equalDates && activeStep === 2 && (
            <Box
              sx={{
                display: "flex",
                width: "90%",
                justifyContent: "center",
                top: "90%",
                position: "absolute",
              }}
            >
              <Alert color="warning" sx={{ borderRadius: "15px" }}>
                <Typography>
                  {translation.get(language, "Search update text")}
                </Typography>
              </Alert>
            </Box>
          )}
        </Container>
      </>
    );
}

function PoweredByUrent() {
  return (
    <Stack
      sx={{
        alignItems: "center",
        justifyContent: "flex-end",
      }}
      direction={"row"}
    >
      <Box paddingBottom={"5px"}>
        <Typography sx={{ fontSize: "12px" }}>Powered by</Typography>
      </Box>
      <UrentLogo />
    </Stack>
  );
}

function SearchButton(props) {
  const { searchButtonMobileStyles, searchButtonDesktopStyles } = props;
  const { language, setInitSearch, initSearch, mobileViewPort } =
    useContext(AppContext);
  const theme = useTheme();
  return (
    <Box
      sx={mobileViewPort ? searchButtonMobileStyles : searchButtonDesktopStyles}
      onClick={() => {
        if (!initSearch) {
          setInitSearch(true);
        }
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.primary.buttonText,
          fontSize: "20px",
        }}
      >
        {translation.get(language, "Search label")}
      </Typography>
    </Box>
  );
}
